import { useState, type ReactNode } from 'react';
import Form, { Field } from '@admin/components/Form';
import Card from '@admin/components/Card';
import Pill from '@admin/components/Pill';
import { useForm, type FormErrors } from '@admin/hooks/useForm';
import { KNOWN_CATEGORIES } from './categories';
import ChipListEditor from './ChipListEditor';
import { questionLanguageVisibility } from './contentLanguages';
import { Difficulty, QuestionContentFlag, QuestionType } from '@games/word-clash/shared/types';
import type { CreateQuestionRequest, QuestionAssistPatch } from '@games/word-clash/shared/types';

/**
 * Form values mirror the admin-facing Question shape. Answer lists are held as
 * `string[]` and edited via {@link ChipListEditor} (removable chips); the alias
 * maps are `{alias, canonical}[]` rows. On submit the arrays pass straight
 * through and the alias rows flip back into `Record<string, string>`.
 */
export interface QuestionFormValues {
  type: QuestionType;
  difficulty: Difficulty;
  category: string;
  promptEn: string;
  promptTr: string;
  validAnswers: string[];
  validAnswersTr: string[];
  specialAnswers: string[];
  specialAnswersTr: string[];
  plausibleWrongAnswers: string[];
  plausibleWrongAnswersTr: string[];
  exampleHint: string;
  exampleHintTr: string;
  aliases: AliasPair[];
  aliasesTr: AliasPair[];
  contentFlags: QuestionContentFlag[];
}

/** The answer-list fields, used for chip editors + assistant-apply highlights. */
type ListField =
  | 'validAnswers'
  | 'validAnswersTr'
  | 'specialAnswers'
  | 'specialAnswersTr'
  | 'plausibleWrongAnswers'
  | 'plausibleWrongAnswersTr';

/** Display labels + hints for each content flag (closed enum set). */
const CONTENT_FLAG_OPTIONS: ReadonlyArray<{
  value: QuestionContentFlag;
  label: string;
  hint: string;
}> = [
  {
    value: QuestionContentFlag.EnOnly,
    label: 'English only',
    hint: 'Excluded from Turkish matches.',
  },
  {
    value: QuestionContentFlag.TrOnly,
    label: 'Turkish only',
    hint: 'Excluded from English matches.',
  },
  {
    value: QuestionContentFlag.NeedsReview,
    label: 'Needs review',
    hint: 'Flag for follow-up content review.',
  },
];

export interface AliasPair {
  alias: string;
  canonical: string;
}

export interface QuestionFormSubmitPayload extends CreateQuestionRequest {
  parsedValidAnswers: string[];
}

interface QuestionFormProps {
  initial: QuestionFormValues;
  /** On edit, the type field is fixed server-side — lock it and show a hint. */
  typeLocked?: boolean;
  onSubmit: (payload: QuestionFormSubmitPayload) => void | Promise<void>;
  submitLabel: string;
  submitting?: boolean;
  /** Optional server-side error message displayed above the submit button. */
  serverError?: string | null;
  /** Optional secondary action slot (e.g. an Active toggle on Edit). */
  extraActions?: React.ReactNode;
  /**
   * Optional AI-assistant slot rendered beside the form. Receives the live
   * form values (to send as the draft) and an `applyPatch` callback that maps
   * a proposed {@link QuestionAssistPatch} back into the form fields. Rendered
   * OUTSIDE the `<form>` element so its controls never submit the question.
   */
  renderAssistant?: (ctx: {
    values: QuestionFormValues;
    applyPatch: (patch: QuestionAssistPatch) => void;
  }) => ReactNode;
}

/**
 * Full question editor. Split into labelled sections (Metadata · English
 * content · Turkish content) with an inline alias editor so admins can
 * edit every field — prior version locked TR answers + aliases + special
 * answers + plausible-wrong to read-only.
 */
export default function QuestionForm({
  initial,
  typeLocked = false,
  onSubmit,
  submitLabel,
  submitting,
  serverError,
  extraActions,
  renderAssistant,
}: QuestionFormProps) {
  const { values, errors, setField, setErrors } =
    useForm<QuestionFormValues>(initial);

  // Per-list-field set of items the assistant just added, so the chips that
  // changed are visually flagged after "Apply". Cleared when the admin edits
  // that field by hand.
  const [applied, setApplied] = useState<Partial<Record<ListField, string[]>>>(
    {},
  );

  // Derived from the LIVE form values, not the loaded question — ticking
  // "English only" collapses the Turkish answer editors immediately, so the
  // admin sees what the flag actually means before saving.
  const { showEnglish, showTurkish } = questionLanguageVisibility(
    values.contentFlags,
  );

  /** Set a list field and clear its assistant-applied highlight. */
  function setList(field: ListField, items: string[]) {
    setField(field, items);
    setApplied((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }

  /**
   * Map an AI-proposed patch into the form fields. Only the keys present on the
   * patch are touched. For list fields we also record which entries are NEW
   * (vs. what was there) so the chips light up — the admin sees exactly what
   * "Apply" changed. Applying is otherwise indistinguishable from manual entry.
   */
  function applyPatch(patch: QuestionAssistPatch) {
    if (patch.promptEn !== undefined) setField('promptEn', patch.promptEn);
    if (patch.promptTr !== undefined) setField('promptTr', patch.promptTr);
    if (patch.category !== undefined) setField('category', patch.category);
    if (patch.difficulty !== undefined) setField('difficulty', patch.difficulty);

    const nextApplied: Partial<Record<ListField, string[]>> = {};
    const applyList = (field: ListField, incoming: string[] | undefined) => {
      if (incoming === undefined) return;
      const prevSet = new Set(values[field].map((s) => s.toLowerCase()));
      nextApplied[field] = incoming.filter((s) => !prevSet.has(s.toLowerCase()));
      setField(field, incoming);
    };
    // Flags FIRST, then gate on the RESULTING visibility.
    //
    // The gate must not read the render-scope `showEnglish`/`showTurkish`,
    // which describe the question as it was BEFORE this patch. A patch that
    // both opens a language and supplies that language's content (assistant
    // asked to make an en-only question bilingual) would otherwise skip every
    // Turkish write and then clear the flag — saving a bilingual question with
    // an empty `validAnswersTr`. Nothing downstream catches that: the server
    // only requires TR ≥ 5 for `tr-only`, so matchmaking would go on to serve
    // an unanswerable question into TR matches.
    const nextFlags = patch.contentFlags ?? values.contentFlags;
    if (patch.contentFlags !== undefined)
      setField('contentFlags', [...patch.contentFlags]);
    const { showEnglish: canWriteEn, showTurkish: canWriteTr } =
      questionLanguageVisibility(nextFlags);

    // Language-gated, same as the editors themselves. Without this the
    // assistant could apply content for a language the question is never
    // served in — and the admin would never see it, because the
    // applied-highlight chips render inside the collapsed section.
    if (canWriteEn) {
      applyList('validAnswers', patch.validAnswers);
      applyList('specialAnswers', patch.specialAnswers);
      applyList('plausibleWrongAnswers', patch.plausibleWrongAnswers);
    }
    if (canWriteTr) {
      applyList('validAnswersTr', patch.validAnswersTr);
      applyList('specialAnswersTr', patch.specialAnswersTr);
      applyList('plausibleWrongAnswersTr', patch.plausibleWrongAnswersTr);
    }
    setApplied((prev) => ({ ...prev, ...nextApplied }));

    if (canWriteEn && patch.answerAliases !== undefined)
      setField('aliases', mapToAliasPairs(patch.answerAliases));
    if (canWriteTr && patch.answerAliasesTr !== undefined)
      setField('aliasesTr', mapToAliasPairs(patch.answerAliasesTr));
  }

  function validate(): FormErrors<QuestionFormValues> | null {
    const next: FormErrors<QuestionFormValues> = {};

    // Both prompts stay required regardless of the flags: the server schema
    // and the `Question` model both mark them required, and the prompt is what
    // the admin list + search render. Only ANSWER content is language-gated.
    if (!values.promptEn.trim()) next.promptEn = 'English prompt is required.';
    if (!values.promptTr.trim()) next.promptTr = 'Turkish prompt is required.';

    // Keys off the RAW flag, not the fail-open display state. The server
    // branches on `contentFlags.includes(TrOnly)` with no fail-open (both the
    // Zod refinement and the model's pre-validate hook), so a both-flags row
    // would otherwise be blocked client-side by an error the server never
    // raises — or pass the client and then 400. Fail-open is right for
    // DISPLAY; validation must not disagree with the two server enforcers.
    if (values.contentFlags.includes(QuestionContentFlag.TrOnly)) {
      if (values.validAnswersTr.length < 5) {
        next.validAnswersTr = `A Turkish-only question needs at least 5 Turkish valid answers (you have ${values.validAnswersTr.length}).`;
      }
    } else if (values.validAnswers.length < 5) {
      next.validAnswers = `At least 5 valid answers are required (you have ${values.validAnswers.length}).`;
    }

    if (!Object.values(QuestionType).includes(values.type)) {
      next.type = 'Pick a valid question type.';
    }
    if (!Object.values(Difficulty).includes(values.difficulty)) {
      next.difficulty = 'Pick a valid difficulty.';
    }

    return Object.keys(next).length === 0 ? null : next;
  }

  function aliasPairsToMap(pairs: AliasPair[]): Record<string, string> {
    const out: Record<string, string> = {};
    for (const p of pairs) {
      const alias = p.alias.trim().toLowerCase();
      const canonical = p.canonical.trim();
      if (!alias || !canonical) continue;
      out[alias] = canonical;
    }
    return out;
  }

  function handleSubmit() {
    const validationErrors = validate();
    if (validationErrors) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});

    void onSubmit({
      type: values.type,
      difficulty: values.difficulty,
      category: values.category.trim() || 'general',
      promptEn: values.promptEn.trim(),
      promptTr: values.promptTr.trim(),
      validAnswers: values.validAnswers,
      validAnswersTr: values.validAnswersTr,
      specialAnswers: values.specialAnswers,
      specialAnswersTr: values.specialAnswersTr,
      plausibleWrongAnswers: values.plausibleWrongAnswers,
      plausibleWrongAnswersTr: values.plausibleWrongAnswersTr,
      exampleHint: values.exampleHint.trim(),
      exampleHintTr: values.exampleHintTr.trim(),
      answerAliases: aliasPairsToMap(values.aliases),
      answerAliasesTr: aliasPairsToMap(values.aliasesTr),
      contentFlags: values.contentFlags,
      parsedValidAnswers: values.validAnswers,
    });
  }

  function toggleContentFlag(flag: QuestionContentFlag) {
    const has = values.contentFlags.includes(flag);
    setField(
      'contentFlags',
      has
        ? values.contentFlags.filter((f) => f !== flag)
        : [...values.contentFlags, flag],
    );
  }

  function setAliasPairs(
    field: 'aliases' | 'aliasesTr',
    pairs: AliasPair[],
  ) {
    setField(field, pairs);
  }

  return (
    <div className="flex flex-col lg:flex-row gap-6 items-start">
    <Form onSubmit={handleSubmit} testId="question-form">
      <div className="space-y-4 max-w-4xl">
        {/* Metadata ------------------------------------------------- */}
        <FormSection title="Metadata">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field
              label="Type"
              htmlFor="question-type"
              error={errors.type ?? null}
              required
              hint={
                typeLocked
                  ? 'Locked on edit — server does not re-categorise type after creation.'
                  : undefined
              }
            >
              <select
                id="question-type"
                value={values.type}
                onChange={(e) =>
                  setField('type', e.target.value as QuestionType)
                }
                disabled={submitting || typeLocked}
                data-testid="question-type"
                className="mt-1 block w-full rounded border border-slate-300 px-2 py-1.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400 disabled:bg-slate-100 disabled:text-slate-500"
              >
                <option value={QuestionType.Category}>Category</option>
                <option value={QuestionType.Prefix}>Prefix</option>
              </select>
            </Field>

            <Field
              label="Difficulty"
              htmlFor="question-difficulty"
              error={errors.difficulty ?? null}
              required
            >
              <select
                id="question-difficulty"
                value={values.difficulty}
                onChange={(e) =>
                  setField('difficulty', e.target.value as Difficulty)
                }
                disabled={submitting}
                data-testid="question-difficulty"
                className="mt-1 block w-full rounded border border-slate-300 px-2 py-1.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400"
              >
                <option value={Difficulty.Easy}>Easy</option>
                <option value={Difficulty.Medium}>Medium</option>
                <option value={Difficulty.Hard}>Hard</option>
              </select>
            </Field>

            <Field
              label="Category"
              htmlFor="question-category"
              hint="Pick a known category or type a new slug. Used by the matchmaker and the admin filter."
            >
              {/* Combobox pattern — native <datalist> gives keyboard-accessible
                  search, preserves free-text fallback for new category slugs,
                  and ships with zero JS dependencies. The DB stores the slug
                  that ends up in the input, not the datalist label. */}
              <input
                id="question-category"
                type="text"
                list="question-category-options"
                value={values.category}
                onChange={(e) => setField('category', e.target.value)}
                disabled={submitting}
                placeholder="general"
                autoComplete="off"
                data-testid="question-category"
                className="mt-1 block w-full rounded border border-slate-300 px-2 py-1.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400"
              />
              <datalist id="question-category-options">
                {KNOWN_CATEGORIES.map((c) => (
                  <option
                    key={c.slug}
                    value={c.slug}
                    label={c.label}
                  />
                ))}
              </datalist>
            </Field>
          </div>

          <Field
            label="Content flags"
            htmlFor="question-content-flags"
            hint="Language/lifecycle gating. 'English only' / 'Turkish only' restrict which language matches serve this question, and hide that language's answer editors below."
          >
            <div
              id="question-content-flags"
              role="group"
              aria-label="Content flags"
              data-testid="question-content-flags"
              className="mt-1 flex flex-wrap gap-3"
            >
              {CONTENT_FLAG_OPTIONS.map((opt) => {
                const checked = values.contentFlags.includes(opt.value);
                return (
                  <label
                    key={opt.value}
                    title={opt.hint}
                    className={
                      'inline-flex items-center gap-2 rounded border px-3 py-1.5 text-sm cursor-pointer select-none ' +
                      (checked
                        ? 'border-slate-800 bg-slate-800 text-white'
                        : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50')
                    }
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleContentFlag(opt.value)}
                      disabled={submitting}
                      data-testid={`question-content-flag-${opt.value}`}
                      className="sr-only"
                    />
                    {opt.label}
                  </label>
                );
              })}
            </div>
          </Field>
        </FormSection>

        {/* English content ---------------------------------------- */}
        <FormSection
          title={
            <span className="flex items-center gap-2">
              English content
              <Pill variant="info" size="sm">
                EN
              </Pill>
              {!showEnglish && <NotServedPill />}
            </span>
          }
        >
          <Field
            label="Prompt"
            htmlFor="question-prompt-en"
            error={errors.promptEn ?? null}
            required
            hint={
              showEnglish
                ? undefined
                : 'Still required — the prompt is what the admin list and search render. It is never shown to a player while this question is Turkish-only.'
            }
          >
            <input
              id="question-prompt-en"
              type="text"
              value={values.promptEn}
              onChange={(e) => setField('promptEn', e.target.value)}
              disabled={submitting}
              data-testid="question-prompt-en"
              className="mt-1 block w-full rounded border border-slate-300 px-2 py-1.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400"
            />
          </Field>

          {showEnglish && (
            <>
            <Field
              label="Example hint"
              htmlFor="question-example-hint"
              hint="Optional. Shown to both players in preview + gameplay to illustrate the answer format. Rejected as a submission, so it never scores — fine to use an obvious valid answer."
            >
              <input
                id="question-example-hint"
                type="text"
                value={values.exampleHint}
                onChange={(e) => setField('exampleHint', e.target.value)}
                disabled={submitting}
                maxLength={200}
                placeholder="e.g. apple"
                data-testid="question-example-hint"
                className="mt-1 block w-full rounded border border-slate-300 px-2 py-1.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400"
              />
            </Field>

            <ChipListEditor
              id="question-valid-answers"
              label="Valid answers"
              required
              items={values.validAnswers}
              onChange={(items) => setList('validAnswers', items)}
              disabled={submitting}
              error={errors.validAnswers ?? null}
              minimumHint="Server rule: at least 5 unique answers."
              testId="question-valid-answers"
              countTestId="question-valid-answers-count"
              highlight={applied.validAnswers}
            />

            <ChipListEditor
              id="question-special-answers"
              label="Special answers"
              items={values.specialAnswers}
              onChange={(items) => setList('specialAnswers', items)}
              disabled={submitting}
              hint="Answers that score but trigger a bonus or side-effect at match time."
              testId="question-special-answers"
              highlight={applied.specialAnswers}
            />

            <ChipListEditor
              id="question-plausible-wrong-answers"
              label="Plausible wrong answers"
              items={values.plausibleWrongAnswers}
              onChange={(items) => setList('plausibleWrongAnswers', items)}
              disabled={submitting}
              hint="Used by the bot opponent to pick believable wrong guesses. Do NOT include valid answers here."
              testId="question-plausible-wrong-answers"
              highlight={applied.plausibleWrongAnswers}
            />

            <AliasEditor
              label="Aliases (alias → canonical answer)"
              pairs={values.aliases}
              onChange={(pairs) => setAliasPairs('aliases', pairs)}
              disabled={submitting}
              testId="question-aliases"
            />
            </>
          )}
        </FormSection>

        {/* Turkish content ---------------------------------------- */}
        <FormSection
          title={
            <span className="flex items-center gap-2">
              Turkish content
              <Pill variant="info" size="sm">
                TR
              </Pill>
              {!showTurkish && <NotServedPill />}
            </span>
          }
        >
          <Field
            label="Prompt"
            htmlFor="question-prompt-tr"
            error={errors.promptTr ?? null}
            required
            hint={
              showTurkish
                ? undefined
                : 'Still required — the prompt is what the admin list and search render. It is never shown to a player while this question is English-only.'
            }
          >
            <input
              id="question-prompt-tr"
              type="text"
              value={values.promptTr}
              onChange={(e) => setField('promptTr', e.target.value)}
              disabled={submitting}
              data-testid="question-prompt-tr"
              className="mt-1 block w-full rounded border border-slate-300 px-2 py-1.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400"
            />
          </Field>

          {showTurkish && (
            <>
            <Field
              label="Example hint"
              htmlFor="question-example-hint-tr"
              hint="Optional Turkish example. Shown only in TR matches — an English-only hint is never shown to TR players."
            >
              <input
                id="question-example-hint-tr"
                type="text"
                value={values.exampleHintTr}
                onChange={(e) => setField('exampleHintTr', e.target.value)}
                disabled={submitting}
                maxLength={200}
                placeholder="örn. elma"
                data-testid="question-example-hint-tr"
                className="mt-1 block w-full rounded border border-slate-300 px-2 py-1.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400"
              />
            </Field>

            <ChipListEditor
              id="question-valid-answers-tr"
              label="Valid answers"
              items={values.validAnswersTr}
              onChange={(items) => setList('validAnswersTr', items)}
              disabled={submitting}
              error={errors.validAnswersTr ?? null}
              hint="Mirrors the English list for Turkish-language matches."
              testId="question-valid-answers-tr"
              highlight={applied.validAnswersTr}
            />

            <ChipListEditor
              id="question-special-answers-tr"
              label="Special answers"
              items={values.specialAnswersTr}
              onChange={(items) => setList('specialAnswersTr', items)}
              disabled={submitting}
              testId="question-special-answers-tr"
              highlight={applied.specialAnswersTr}
            />

            <ChipListEditor
              id="question-plausible-wrong-answers-tr"
              label="Plausible wrong answers (Turkish)"
              items={values.plausibleWrongAnswersTr}
              onChange={(items) => setList('plausibleWrongAnswersTr', items)}
              disabled={submitting}
              hint="Used by the bot opponent in TR matches. MUST be Turkish — leaving English entries here would surface them as wrong answers to TR players."
              testId="question-plausible-wrong-answers-tr"
              highlight={applied.plausibleWrongAnswersTr}
            />

            <AliasEditor
              label="Aliases (Turkish)"
              pairs={values.aliasesTr}
              onChange={(pairs) => setAliasPairs('aliasesTr', pairs)}
              disabled={submitting}
              testId="question-aliases-tr"
            />
            </>
          )}
        </FormSection>

        {serverError && (
          <div
            role="alert"
            data-testid="question-form-server-error"
            className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2"
          >
            {serverError}
          </div>
        )}

        <div className="flex items-center justify-between gap-3">
          <div>{extraActions}</div>
          <button
            type="submit"
            disabled={submitting}
            data-testid="question-form-submit"
            className="text-sm rounded bg-slate-800 text-white px-4 py-2 hover:bg-slate-700 disabled:opacity-50"
          >
            {submitting ? 'Saving…' : submitLabel}
          </button>
        </div>
      </div>
    </Form>
      {renderAssistant && (
        <div
          className="w-full lg:w-[22rem] lg:sticky lg:top-4 shrink-0"
          data-testid="question-assistant-slot"
        >
          {renderAssistant({ values, applyPatch })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/**
 * Marks a language section whose ANSWER content is switched off by an
 * `en-only` / `tr-only` flag. The section keeps its prompt field (required by
 * the server and the model) but drops every answer / hint / alias editor, so
 * nobody spends time authoring content that will never be served.
 */
function NotServedPill() {
  return (
    <span
      className="rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
      data-testid="question-language-not-served"
    >
      Not served in this language
    </span>
  );
}

function FormSection({
  title,
  children,
}: {
  title: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card padding="lg">
      <div className="space-y-4">
        <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
        {children}
      </div>
    </Card>
  );
}

/**
 * Row-based alias editor. Each alias pair is a `{alias, canonical}` row with
 * its own remove button; the bottom row has an "Add alias" button. This is
 * deliberately simple — no drag-reorder, no duplicate-detection UI — since
 * the server already normalises input (lowercases keys, dedupes).
 */
function AliasEditor({
  label,
  pairs,
  onChange,
  disabled,
  testId,
}: {
  label: string;
  pairs: AliasPair[];
  onChange: (pairs: AliasPair[]) => void;
  disabled?: boolean;
  testId: string;
}) {
  function updateRow(index: number, patch: Partial<AliasPair>) {
    onChange(pairs.map((p, i) => (i === index ? { ...p, ...patch } : p)));
  }

  function removeRow(index: number) {
    onChange(pairs.filter((_, i) => i !== index));
  }

  function addRow() {
    onChange([...pairs, { alias: '', canonical: '' }]);
  }

  return (
    <div data-testid={testId}>
      <div className="flex items-baseline justify-between">
        <span className="text-xs font-medium text-slate-700">{label}</span>
        <span className="text-xs text-slate-500">
          {pairs.length} {pairs.length === 1 ? 'alias' : 'aliases'}
        </span>
      </div>
      <div className="mt-1 space-y-2">
        {pairs.map((p, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              type="text"
              value={p.alias}
              onChange={(e) =>
                updateRow(i, { alias: e.target.value })
              }
              disabled={disabled}
              placeholder="alias"
              data-testid={`${testId}-alias-${i}`}
              className="flex-1 min-w-0 rounded border border-slate-300 px-2 py-1 text-sm text-slate-900 font-mono focus:outline-none focus:ring-2 focus:ring-slate-400"
            />
            <span className="text-slate-400">→</span>
            <input
              type="text"
              value={p.canonical}
              onChange={(e) =>
                updateRow(i, { canonical: e.target.value })
              }
              disabled={disabled}
              placeholder="canonical valid answer"
              data-testid={`${testId}-canonical-${i}`}
              className="flex-1 min-w-0 rounded border border-slate-300 px-2 py-1 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400"
            />
            <button
              type="button"
              onClick={() => removeRow(i)}
              disabled={disabled}
              data-testid={`${testId}-remove-${i}`}
              aria-label="Remove alias"
              className="text-xs rounded px-2 py-1 border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-40"
            >
              ×
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={addRow}
          disabled={disabled}
          data-testid={`${testId}-add`}
          className="text-xs rounded px-2 py-1 border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-40"
        >
          + Add alias
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Value-shaping helpers (exported for Edit/New to build QuestionFormValues)
// ---------------------------------------------------------------------------

/* eslint-disable react-refresh/only-export-components -- co-located helpers
   consumed by the component and its unit tests; moving them to a separate
   file would add a second module for two trivial pure functions. */

/**
 * Turn a `Record<alias, canonical>` into an ordered list of AliasPair rows.
 * Preserves insertion order of the object so the rendered UI matches what
 * the admin most recently edited.
 */
export function mapToAliasPairs(map: Record<string, string>): AliasPair[] {
  return Object.entries(map).map(([alias, canonical]) => ({
    alias,
    canonical,
  }));
}

/**
 * Join an answer array into a newline-delimited string. The form no longer
 * uses textareas, but this stays as a shared display helper (e.g. ReviewCard
 * renders read-only answer lists as text).
 */
export function arrayToText(arr: readonly string[]): string {
  return arr.join('\n');
}
